import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import { useUserState, STATE_NAMES, isLiveState } from '../../lib/userState';

/**
 * NationalLocalFunnel - the "national, live locally" funnel block shared by
 * every national guide page. It stays national in content but becomes
 * state-RELEVANT: it leads with the visitor's selected state (header chip),
 * and for a state we're not live in yet it says so honestly while still
 * pointing to where live help runs today.
 */
const NationalLocalFunnel = ({
  funnelTestId,
  eyebrow,
  heading,
  intro,
  cardPrefix,
  states = [],
  crossLink,
}) => {
  const userState = useUserState();
  // Lead with the visitor's state if it's one of the live states shown.
  const ordered = [...states].sort((a, b) => {
    if (a.code === userState) return -1;
    if (b.code === userState) return 1;
    return 0;
  });
  const userIsLiveHere = states.some((s) => s.code === userState);
  const showNonLiveNote = !!userState && !userIsLiveHere && !isLiveState(userState);
  const userStateName = STATE_NAMES[userState] || userState;

  return (
    <section className="bg-[#0a2540] text-white" data-testid={funnelTestId}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-5 h-5 text-[#34d17f]" />
          <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#34d17f]">{eyebrow}</p>
        </div>
        <h2 className="text-[24px] sm:text-[32px] font-bold tracking-tight leading-[1.15] mb-3">{heading}</h2>
        <p className="text-[15px] text-white/70 mb-6 max-w-2xl leading-[1.6]">{intro}</p>

        {showNonLiveNote && (
          <div
            className="mb-6 rounded-2xl border border-[#34d17f]/25 bg-[#34d17f]/[0.08] px-5 py-4"
            data-testid={`${cardPrefix}non-live-note`}
          >
            <p className="text-[14px] text-white/90 leading-[1.6]">
              <span className="font-semibold">BuildPilot isn't live in {userStateName} yet</span> — but it's expanding across Australia, and this national guide applies to you today. Here's where live matching runs right now:
            </p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          {ordered.map((l) => {
            const isYourState = l.code === userState;
            return (
              <div
                key={l.code}
                className={`rounded-2xl border p-5 ${isYourState ? 'border-[#34d17f]/50 bg-[#34d17f]/[0.06]' : 'border-white/15 bg-white/[0.04]'}`}
                data-testid={`${cardPrefix}${l.city.toLowerCase()}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-[12px] uppercase tracking-[0.06em] text-white/50 font-semibold">{l.state}</p>
                  {isYourState && (
                    <span className="text-[10px] font-bold uppercase tracking-[0.05em] text-[#0a2540] bg-[#34d17f] rounded-full px-2 py-0.5" data-testid={`${cardPrefix}your-state`}>
                      Your state
                    </span>
                  )}
                </div>
                <h3 className="text-[18px] font-bold mb-4">{l.city}</h3>
                <div className="flex flex-col gap-2">
                  <Link to={l.primary.to} className="inline-flex items-center justify-between gap-2 rounded-full bg-white text-[#0a2540] px-4 py-2.5 text-[13.5px] font-semibold hover:bg-white/90 transition-colors">
                    {l.primary.label} <ArrowRight className="w-4 h-4" />
                  </Link>
                  {l.secondary && (
                    <Link to={l.secondary.to} className="inline-flex items-center justify-between gap-2 rounded-full border border-white/25 px-4 py-2.5 text-[13.5px] font-semibold hover:bg-white/10 transition-colors">
                      {l.secondary.label} <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {crossLink && (
          <div className="mt-7">
            <Link to={crossLink.to} className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#34d17f] hover:text-white transition-colors" data-testid={crossLink.testid}>
              {crossLink.label} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default NationalLocalFunnel;
